import type { FC } from 'react'
import { useState } from 'react'

import { Dropzone, ExternalLink, Input, MessagePopup, UploadedFileZone } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'
import type { FieldProps } from 'formik'
import { Field } from 'formik'

import { useTranslation } from '@/app/translations/hooks'
import Text from '@/app/translations/Text'
import queries from '@/forms/onboarding/queries'
import onboardingResources from '@/forms/onboarding/resources'
import type { OnboardingFormFields } from '@/forms/onboarding/types'

import useAuthFilesDropzone from './useAuthFilesDropzone'

import './index.styles.scss'

const ACCEPTED_FILE_TYPES = ['jpg', 'jpeg', 'pdf', 'png']

const Waiver: FC<{
  values: OnboardingFormFields
  isEditable: boolean
  onSetFieldValues: (fieldName: string, fileName: string) => void
  onSetFieldError: (fieldName: string, fileName: string) => void
  onSetFieldTouched: (fieldName: string, isTouched: boolean) => void
}> = ({ values, isEditable, onSetFieldValues, onSetFieldError, onSetFieldTouched }) => {
  const translate = useTranslation()
  const [isErrorPopupOpen, setIsErrorPopupOpen] = useState(false)

  const { authorizeUpload, isAuthorizingUpload } = useAuthFilesDropzone({
    type: 'ONBOARDING_AUTH_FORM',
    fileTypes: ACCEPTED_FILE_TYPES,
    onUpload: (fileName: string) => onSetFieldValues('authorizationFileName', fileName),
    onError(err) {
      onSetFieldError('authorizationFile', err)
      onSetFieldTouched('authorizationFile', true)
    },
  })

  const { refetch: authorizeFileDownload, isInitialLoading: isDownloading } = useQuery(
    queries.onboardingFileAuthorization(),
    onboardingResources.authorizeFileDownload,
    {
      enabled: false,
      onError() {
        setIsErrorPopupOpen(true)
      },
    }
  )

  if (values.isNewNumber !== false || values.userIsOwner) return null

  return (
    <div className="waiver">
      <h4>
        <Text id="Fullmakt" />
      </h4>

      {values.authorizationFileName ? (
        <div className={`uploaded-file-buttons ${isEditable ? '' : 'is-disabled'}`}>
          <Field name="authorizationFileName">
            {({ field, form }: FieldProps) => (
              <UploadedFileZone
                documentFilename={field.value}
                isRemovalDisabled={!isEditable}
                isDownloading={isDownloading}
                onDownloadClick={() => authorizeFileDownload()}
                onRemoveClick={() => {
                  form.setFieldValue('authorizationFileName', '')
                  form.setFieldValue('authorizationFile', '')
                  form.setFieldTouched('authorizationFile', false)
                }}
              />
            )}
          </Field>
        </div>
      ) : (
        <div className="dropzone-container">
          <Field name="authorizationFile">
            {({ field, meta }: FieldProps) => (
              <Dropzone
                id="authorizationFile"
                error={meta.error}
                touched={meta.touched}
                activeDragText={<Text id="Slipp fil her for å laste opp" />}
                inActiveDragText={<Text id="Trekk fil her eller trykk for å bla" />}
                isDisabled={!isEditable}
                isUploading={isAuthorizingUpload}
                onDrop={authorizeUpload}
                {...field}
              />
            )}
          </Field>

          <Field name="authorizationFileName">
            {({ field }: FieldProps) => (
              <Input id="authorizationFileName" type="hidden" {...field} />
            )}
          </Field>

          <p>
            <Text
              id="Dersom du ikke allerede har mottatt en fullmakt, fyll ut {link}."
              link={
                <ExternalLink href="/fullmaktsmal.pdf">
                  <Text id="dette skjemaet" />
                </ExternalLink>
              }
            />

            <br />

            <Text id="Last deretter opp et bilde, en scan eller en pdf av fullmakten." />
          </p>
        </div>
      )}

      <MessagePopup
        title={translate('File download error')}
        message={translate(
          'We encountered a problem while downloading your file. Please refresh and try again.'
        )}
        isOpen={isErrorPopupOpen}
        onClose={() => setIsErrorPopupOpen(false)}
      />
    </div>
  )
}

export default Waiver
